import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Alert } from '@app/ui';
import { Notification } from './Notifications';

export const useAlert = (onClose?: (id: Notification['id']) => void) => {
    const emptyObject = useMemo(() => ({}), []);
    const [alertList, setAlertList] =
        useState<Record<string, Notification>>(emptyObject);
    const timeoutRefs = useRef<Record<string, number>>({});
    const onCloseRef = useRef(onClose);

    // Update the ref when onClose changes
    React.useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    const open = useCallback(
        (key: Notification['id'], notification: Notification) => {
            setAlertList(current => ({
                ...current,
                [key]: notification,
            }));

            if (notification.shouldAutoClose) {
                timeoutRefs.current[key] = window.setTimeout(() => {
                    destroy(key);
                }, 10000);
            }
        },
        [],
    );

    const destroy = useCallback((key: Notification['id']) => {
        // Call onClose through ref to avoid dependency issues
        if (onCloseRef.current) {
            onCloseRef.current(key);
        }

        if (timeoutRefs.current[key]) {
            window.clearTimeout(timeoutRefs.current[key]);
            delete timeoutRefs.current[key];
        }

        setAlertList(current => {
            const newList = { ...current };
            delete newList[key];
            return newList;
        });
    }, []);

    const destroyAll = useCallback(() => {
        // Clear all timeouts
        Object.values(timeoutRefs.current).forEach(timeout => {
            window.clearTimeout(timeout);
        });
        timeoutRefs.current = {};

        setAlertList(emptyObject);
    }, [alertList]);

    // Memoize the alertApi object to prevent it from being recreated
    const alertApi = useMemo(
        () => ({
            open,
            destroy,
            destroyAll,
        }),
        [open, destroy, destroyAll],
    );

    const contextHolder = useMemo(() => {
        const alertValues = Object.values(alertList);
        if (alertValues.length > 0) {
            return (
                <div
                    style={{
                        position: 'fixed',
                        top: 80,
                        right: 50,
                        zIndex: 9999,
                    }}
                >
                    {alertValues.map(notification => (
                        <Alert
                            key={notification.id}
                            className="notifications-alert"
                            closable
                            showIcon
                            message={notification.text}
                            description={notification.options.description}
                            type={
                                notification.options.context === 'danger'
                                    ? 'error'
                                    : notification.options.context
                            }
                            afterClose={() => destroy(notification.id)}
                        />
                    ))}
                </div>
            );
        } else {
            return null;
        }
    }, [alertList, destroy]);

    return {
        alertApi,
        alertContextHolder: contextHolder,
    };
};
